import { FC, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { createZodFormResolver, Form, lt, useDialog } from '@mews/framework';
import { useCommanderSelector } from '@mews-commander/utils';
import { TextKey } from '@mews-commander/enums';
import { SchemaFormDefinition, SchemaFormFields } from '@mews-commander/form';
import { deleteServiceOrderNotes } from '@mews-commander/legacy-entities/src/ducks/serviceOrderNotes';
import { getNoteIdsToBeDeleted } from '../../../selectors';
import { FormLayout } from '../../FormLayout';
import { resetNoteIdsToBeDeleted, setNoteIdsToBeDeleted } from '../../../actions';
import { CompactedNote, NotesFormSchema, NotesFormValues } from './types';
import { NoteLabel } from './NoteLabel';
import { DeleteNotesDialog } from './DeleteNotesDialog';
import { useDeleteNotesMutation } from './useDeleteNotesMutation';
import { getCheckedReservationsServiceOrderNotes } from './selectors';
import { useUpdateNotes } from './useUpdateNotes';

interface NotesFormProps {
    reservationIds: string[];
    scope: string;
    onSuccess: () => void;
}

export const NotesForm: FC<NotesFormProps> = ({ reservationIds, scope, onSuccess }) => {
    const defaultNotes = useCommanderSelector((state) => getCheckedReservationsServiceOrderNotes(state, { scope }));
    const noteIdsToBeDeleted = useCommanderSelector((state) => getNoteIdsToBeDeleted(state, { scope }));
    const [{ active, onClose }, openDialog] = useDialog();
    const dispatch = useDispatch();

    const deleteMutation = useDeleteNotesMutation(reservationIds);
    const { updateNotes, isLoading: isLoadingUpdateNotes } = useUpdateNotes(reservationIds, onSuccess);

    const onSubmit = (values: NotesFormValues) => {
        void updateNotes(values);
    };

    const onConfirmDelete = () => {
        onCloseDeleteDialog();

        deleteMutation.mutate(
            { noteIds: noteIdsToBeDeleted },
            {
                onSuccess: () => {
                    dispatch(deleteServiceOrderNotes(noteIdsToBeDeleted));
                    onSuccess();
                },
            }
        );
    };

    const onDelete = useCallback(
        (noteIds: string[]) => {
            dispatch(setNoteIdsToBeDeleted({ noteIds, scope }));
            openDialog();
        },
        [scope, dispatch, openDialog]
    );

    const onCloseDeleteDialog = () => {
        onClose();
        dispatch(resetNoteIdsToBeDeleted(scope));
    };

    const notesRender = useCallback(
        (props, meta) =>
            defaultNotes.map((note: CompactedNote, index: number) => (
                <SchemaFormFields.Field
                    key={note.id}
                    name={`${props.name}.${index}.text`}
                    schema={meta.schema}
                    fieldProps={{
                        label: <NoteLabel note={note} scope={scope} onDelete={onDelete} />,
                        field: { type: 'textarea' },
                    }}
                />
            )),
        [defaultNotes, scope, onDelete]
    );

    const formDefinition: SchemaFormDefinition<typeof NotesFormSchema> = {
        fields: {
            notes: {
                render: notesRender,
            },
            noteText: {
                label: lt(TextKey.AddNote),
                field: {
                    type: 'textarea',
                },
            },
        },
    };

    return (
        <>
            <Form
                onSubmit={onSubmit}
                resolver={createZodFormResolver(NotesFormSchema)}
                values={{ notes: defaultNotes }}
                dataTestForm={`notes-form-${scope}`}
            >
                <FormLayout title={lt('Notes')} loading={isLoadingUpdateNotes || deleteMutation.isPending}>
                    <SchemaFormFields schema={NotesFormSchema} formDefinition={formDefinition} />
                </FormLayout>
            </Form>
            <DeleteNotesDialog active={active} onClose={onCloseDeleteDialog} onConfirm={onConfirmDelete} />
        </>
    );
};
